import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/provider';
import { STALE_TIMES } from '@/api/queryClient';
import { queryKeys } from '@/api/queryKeys';
import type { BackgroundPushStatus, NotificationPreferences } from '@/api/types';
import { localZone } from '@/features/calendar/zone';
import { davFailureKind } from '@/features/dav/failure';
import { isDesktopApp } from '@/utils/desktop';
import {
  MAX_TIMER_MS,
  markFired,
  planReminders,
  reminderBody,
  reminderUrl,
  remindersAllowed,
  wasFired,
  type PlannedReminder,
} from './calendarReminders';
import { createLeaderElection, LEADER_CHANNEL_NAME } from './leader';
import {
  alreadyShown,
  BACKGROUND_PUSH_QUERY,
  hasPushSubscription,
  notificationsSupported,
  raiseReminderNotice,
  serviceWorkerRegistration,
} from './reminderNotice';

/**
 * The open app's own calendar reminders — the fallback to the server's push.
 *
 * The server pushes a reminder at its trigger time to an owner with
 * background notifications on. Everyone else — no background notifications, a
 * browser that never registered for push, the desktop app (whose webview has
 * no Web Push at all) — hears about a reminder only if the open app raises it,
 * so this does, once per mailbox: the primary and every connected mailbox
 * whose password still works (the shell mounts one per account). A mailbox
 * whose calendar answers `409` (password to re-enter) or `501` (no calendar
 * for its host, or not served for connected mailboxes) is skipped quietly:
 * no retry, and no ten-minute refetch of an answer that will not change.
 *
 * - **Data.** Its own query over the next 48 hours, under the calendar's
 *   `eventsScope`, so an edit in the calendar (which invalidates that scope,
 *   features/calendar/mutations.ts) re-plans at once; otherwise it refetches
 *   every ten minutes. Longest reminder offered is a day before, so 48 hours
 *   ahead always covers the next 24 hours of triggers.
 * - **One window.** Every window of the browser runs this, and only the
 *   elected one sets timers — per mailbox, so two mailboxes' hooks in one
 *   window do not out-vote each other. The fired set (calendarReminders.ts) is shared
 *   through storage, so a new leader does not repeat what the old one raised.
 * - **Checked when it fires, not when it is planned.** Preferences, quiet
 *   hours, snooze and whether the server now covers this browser can all
 *   change during the hours a timer waits.
 * - **Shown even while the app is in front**, unlike new mail: nothing on the
 *   mail screen says the meeting starts in ten minutes.
 */

/** Re-plan (and, at the top of the hour, move the window) this often. */
const REFRESH_MS = 10 * 60_000;
/** From the start of this hour: at least 48 hours past now, all hour long. */
const WINDOW_MS = 49 * 60 * 60_000;
const HOUR_MS = 60 * 60_000;

/** A calendar answer that asking again will not change (see failure.ts). */
function permanentlyRefused(error: unknown): boolean {
  if (error === null || error === undefined) return false;
  const kind = davFailureKind(error);
  return kind !== 'transient' && kind !== 'failed';
}

function hourFloor(now: number): number {
  return Math.floor(now / HOUR_MS) * HOUR_MS;
}

/**
 * Whether the server will push this reminder to this browser.
 *
 * GET /push/background must say the server sends REMINDERS
 * (`calendarReminders`), not merely that background notifications are on: a
 * server that pushes new mail but no reminders (one from before reminders,
 * which omits the field) would leave no reminder at all if this stood aside. That is also why
 * the new-mail poll's `backgroundPush` is not consulted here. Then this
 * browser must hold a subscription for the push to land on. The desktop app
 * never does: its webview has no Web Push, whatever the account says.
 */
async function serverCoversThisBrowser(
  client: QueryClient,
  registration: ServiceWorkerRegistration | undefined,
): Promise<boolean> {
  if (isDesktopApp()) return false;
  const status = client.getQueryData<BackgroundPushStatus>(BACKGROUND_PUSH_QUERY);
  if (status?.status !== 'on' || status.calendarReminders !== true) return false;
  return hasPushSubscription(registration);
}

interface FireContext {
  client: QueryClient;
  accountId: string;
  zone: string;
  navigate: RefObject<NavigateFunction>;
}

/** Raises one reminder, if everything that can say no says yes. */
async function fireReminder(item: PlannedReminder, context: FireContext): Promise<void> {
  const { client, accountId, zone } = context;
  const now = Date.now();
  // Never after the event began: a reminder for a meeting in progress is noise.
  if (now >= item.startsAt || wasFired(item.key)) return;
  if (!notificationsSupported() || Notification.permission !== 'granted') return;
  const prefs = client.getQueryData<NotificationPreferences>(
    queryKeys.account(accountId).notificationPrefs(),
  );
  if (!remindersAllowed(prefs, new Date(now), zone)) return;

  const registration = await serviceWorkerRegistration();
  if (await serverCoversThisBrowser(client, registration)) return;
  if (await alreadyShown(registration, item.tag, now)) {
    markFired(item.key, now);
    return;
  }
  // Checked again after the awaits: another path may have got here meanwhile.
  if (wasFired(item.key)) return;
  markFired(item.key, now);

  const { event, title, tag } = item;
  await raiseReminderNotice(
    {
      title,
      body: reminderBody(event, new Date(now), zone),
      tag,
      url: reminderUrl(event.id, event.start, accountId),
      now,
    },
    registration,
    context.navigate,
  );
}

export function useCalendarReminders(accountId: string | undefined): void {
  const api = useApi();
  const client = useQueryClient();
  const id = accountId ?? '';
  const zone = useMemo(() => localZone(), []);

  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const prefs = useQuery({
    queryKey: queryKeys.account(id).notificationPrefs(),
    queryFn: ({ signal }) => api.push.getPreferences(id, signal),
    enabled: id !== '',
    staleTime: STALE_TIMES.metadata,
  });
  // Off means no calendar reads at all. It is checked again at fire time,
  // because it can change while a timer waits.
  const wanted =
    id !== '' &&
    notificationsSupported() &&
    prefs.data?.enabled === true &&
    prefs.data.calendarReminders === true;

  // `windowStart` only changes on the hour, so the query key is stable in
  // between; `tick` re-plans every ten minutes so a trigger that was beyond
  // the scheduling horizon gets its timer even when the data has not changed.
  const [windowStart, setWindowStart] = useState(() => hourFloor(Date.now()));
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!wanted) return;
    const timer = setInterval(() => {
      setWindowStart(hourFloor(Date.now()));
      setTick((current) => current + 1);
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [wanted]);

  const range = useMemo(
    () => ({
      start: new Date(windowStart).toISOString(),
      end: new Date(windowStart + WINDOW_MS).toISOString(),
    }),
    [windowStart],
  );

  const events = useQuery({
    queryKey: queryKeys.account(id).events(range),
    queryFn: ({ signal }) => api.calendar.listEvents({ accountId: id, ...range }, signal),
    enabled: wanted,
    staleTime: STALE_TIMES.list,
    // Stops for an answer that will not change by asking again: a password
    // to re-enter, or no calendar for this mailbox. Anything else (a
    // timeout, a 503) is tried again on the next round.
    refetchInterval: (query) => (permanentlyRefused(query.state.error) ? false : REFRESH_MS),
    // A hidden tab is exactly where a reminder is needed most.
    refetchIntervalInBackground: true,
    // A 501 (no calendar server), a 409 (connected mailbox's password
    // rejected) or a 401 will not improve by asking again; either way there
    // is simply nothing to remind about.
    retry: false,
    // Keeps the timers across the hourly window move.
    placeholderData: keepPreviousData,
  });

  // Read by `serverCoversThisBrowser` from the cache; fetched here so it is
  // there even if the settings page was never opened.
  useQuery({
    queryKey: BACKGROUND_PUSH_QUERY,
    queryFn: ({ signal }) => api.push.getBackgroundPush(signal),
    enabled: wanted && !isDesktopApp(),
    staleTime: STALE_TIMES.metadata,
    retry: false,
  });

  const [leading, setLeading] = useState(false);
  useEffect(() => {
    if (!wanted) return;
    const election = createLeaderElection({
      channelName: `${LEADER_CHANNEL_NAME}:reminders:${id}`,
      onChange: setLeading,
    });
    const onVisibility = (): void => election.refresh();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      election.stop();
      setLeading(false);
    };
  }, [wanted, id]);

  const data = events.data;
  useEffect(() => {
    if (!wanted || !leading || data === undefined) return;
    const now = Date.now();
    const context: FireContext = { client, accountId: id, zone, navigate: navigateRef };
    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (const item of planReminders(data, now, zone)) {
      if (wasFired(item.key)) continue;
      const delay = Math.min(Math.max(item.triggerAt - now, 0), MAX_TIMER_MS);
      timers.push(
        setTimeout(() => {
          if (!cancelled) void fireReminder(item, context);
        }, delay),
      );
    }
    return () => {
      cancelled = true;
      for (const timer of timers) clearTimeout(timer);
    };
  }, [wanted, leading, data, client, id, zone, tick]);
}

/** Mounted by the shell once per mailbox. Renders nothing. */
export function CalendarReminders({ accountId }: { accountId: string }): null {
  useCalendarReminders(accountId);
  return null;
}
